import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MdMenuOpen,
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
} from "react-icons/md";
import "./style.css";
import routes from "../../Utils/routes";

export default function Sidebar({ isOpen, onToggle }) {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isOpen) setExpandedGroups({});
  }, [isOpen]);

  useEffect(() => {
    const currentPath = location.pathname;
    const newExpandedGroups = {};

    routes.forEach((route) => {
      if (route.isGroup && route.children) {
        const hasActiveChild = route.children.some((child) =>
          currentPath === child.path ||
          (currentPath.startsWith(child.path + "/") && child.path !== "/")
        );
        if (hasActiveChild) {
          newExpandedGroups[route.label] = true;
        }
      }
    });

    if (Object.keys(newExpandedGroups).length > 0) {
      setExpandedGroups(newExpandedGroups);
    }
  }, [location.pathname]);

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        if (key !== label) {
          newState[key] = false;
        }
      });
      newState[label] = !prev[label];
      return newState;
    });
  };

  const handleChildClick = () => {
    if (isMobile) onToggle(false);
  };

  const sidebarWidth = isOpen ? "w-64" : "w-16";

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => onToggle(false)}
        />
      )}
      <nav
        data-sidebar
        className={`fixed top-0 left-0 h-full bg-gray-700 text-gray-100 z-50 transition-all duration-300 
          ${isMobile ? "w-64" : sidebarWidth}
          ${isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"}
        `}
        style={{ boxShadow: "2px 0 8px rgba(0,0,0,0.3)" }}
      >
        <div className="h-full flex flex-col">
          <div
            className={`h-16 flex items-center border-b border-gray-600 ${isOpen ? "px-4" : "px-2"
              }`}
          >
            <Link
              to="/dashboard"
              onClick={handleChildClick}
              className="flex items-center gap-3 transition-all duration-200 hover:scale-105"
            >
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-orange-400 hover:shadow-lg">
                G
              </div>
              {(isOpen || isMobile) && (
                <span className="font-bold text-lg transition-colors duration-200">
                  GharPlans
                </span>
              )}
            </Link>
            {!isOpen && !isMobile && (
              <button
                data-sidebar-toggle
                onClick={() => onToggle(true)}
                className="ml-auto p-2 rounded-md transition-all duration-200 hover:bg-gray-600 hover:scale-110 hover:shadow-md"
              >
                <MdMenuOpen size={20} />
              </button>
            )}
            {isMobile && isOpen && (
              <button
                data-sidebar-toggle
                onClick={() => onToggle(false)}
                className="ml-auto p-2 rounded-md transition-all duration-200 hover:bg-gray-600 hover:scale-110 hover:shadow-md"
              >
                <MdMenuOpen size={20} />
              </button>
            )}
          </div>
          <ul className="flex-1 overflow-y-auto p-2 space-y-1">
            {routes
              .filter((item) => item.visible !== false)
              .map((item, idx) => {
                const isActive =
                  location.pathname === item.path ||
                  (location.pathname.startsWith(item.path + "/") &&
                    item.path !== "/");
                if (item.isGroup) {
                  const expanded = expandedGroups[item.label];
                  return (
                    <li key={idx}>
                      <button
                        onClick={() => toggleGroup(item.label)}
                        className={`w-full flex items-center rounded-md px-4 py-3 transition-all duration-200 transform
                        ${isActive
                            ? "bg-orange-500 text-white shadow-lg"
                            : "bg-gray-600 hover:bg-gray-500 hover:shadow-md hover:scale-[1.02] hover:-translate-y-0.5"
                          }`}
                      >
                        <span className="transition-transform duration-200 hover:scale-110 flex-shrink-0">
                          {item.icon}
                        </span>
                        {(isOpen || isMobile) && (
                          <span
                            className="ml-3 transition-all duration-200 flex-1 min-w-0 text-left text-sm leading-tight"
                            style={{
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              hyphens: "auto",
                            }}
                          >
                            {item.label}
                          </span>
                        )}
                        {(isOpen || isMobile) && (
                          <span className="ml-2 transition-transform duration-200 flex-shrink-0">
                            {expanded ? (
                              <MdKeyboardArrowDown className="transition-transform duration-200 hover:scale-110" />
                            ) : (
                              <MdKeyboardArrowRight className="transition-transform duration-200 hover:scale-110" />
                            )}
                          </span>
                        )}
                      </button>
                      {expanded && (
                        <ul className="ml-6 mt-1 space-y-1">
                          {item.children
                            ?.filter((child) => child.visible !== false)
                            .map((child, i) => (
                              <li key={i}>
                                <Link
                                  to={child.path}
                                  onClick={handleChildClick}
                                  className={`block rounded-md px-4 py-2 transition-all duration-200 transform relative overflow-hidden
                                ${location.pathname === child.path
                                      ? "bg-orange-500 text-white shadow-md"
                                      : "hover:bg-gray-600 hover:shadow-sm hover:scale-[1.01] hover:-translate-y-0.5 hover:pl-6"
                                    }
                                before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-orange-400 before:transform before:scale-y-0 before:transition-transform before:duration-200 hover:before:scale-y-100
                              `}
                                >
                                  <span
                                    className="relative z-10 transition-all duration-200 text-xs leading-tight"
                                    style={{
                                      wordBreak: "break-word",
                                      overflowWrap: "break-word",
                                      hyphens: "auto",
                                    }}
                                  >
                                    {child.label}
                                  </span>
                                </Link>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  );
                }
                return (
                  <li key={idx}>
                    <Link
                      to={item.path}
                      onClick={handleChildClick}
                      className={`flex items-center rounded-md px-4 py-3 transition-all duration-200 transform relative overflow-hidden
                      ${isActive
                          ? "bg-orange-500 text-white shadow-lg"
                          : "bg-gray-600 hover:bg-gray-500 hover:shadow-md hover:scale-[1.02] hover:-translate-y-0.5"
                        }
                      before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-orange-400 before:transform before:scale-y-0 before:transition-transform before:duration-200 hover:before:scale-y-100
                    `}
                    >
                      <span className="transition-transform duration-200 hover:scale-110 relative z-10 flex-shrink-0">
                        {item.icon}
                      </span>
                      {(isOpen || isMobile) && (
                        <span
                          className="ml-3 transition-all duration-200 relative z-10 flex-1 min-w-0 text-left text-sm leading-tight"
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </div>
      </nav>
    </>
  );
}
